"use client";

import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function MarketAuthority() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const projects = [{
    id: 1,
    title: "Fix-and-Flip Portfolio",
    location: "CORE ASSETS",
    year: "2024",
    category: "ENGINE 01",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722377/img/fix_and_flip_deals.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722377/img/fix_and_flip_deals.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722377/img/fix_and_flip_deals.png-wvc-sizes",
    alt: "Fix and flip real estate investment property",
    span: "lg:col-span-7",
    aspect: "aspect-[16/10]"
  }, {
    id: 2,
    title: "BRRRR Strategy",
    location: "LONG-TERM",
    year: "2024",
    category: "ENGINE 01",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722377/img/brrrr_strategy.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722377/img/brrrr_strategy.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722377/img/brrrr_strategy.png-wvc-sizes",
    alt: "BRRRR real estate investment strategy",
    span: "lg:col-span-5",
    aspect: "aspect-[5/6]"
  }, {
    id: 3,
    title: "Joint Venture",
    location: "PARTNERSHIP",
    year: "2024",
    category: "ENGINE 02",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722377/img/jv_partnership.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722377/img/jv_partnership.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722377/img/jv_partnership.webp-wvc-sizes",
    alt: "Joint venture partnership meeting",
    span: "lg:col-span-5",
    aspect: "aspect-[5/6]"
  }, {
    id: 4,
    title: "New Construction",
    location: "DEVELOPMENT",
    year: "2024",
    category: "ENGINE 01",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722377/img/new_construction.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722377/img/new_construction.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722377/img/new_construction.webp-wvc-sizes",
    alt: "New construction residential project",
    span: "lg:col-span-7",
    aspect: "aspect-[16/10]"
  }, {
    id: 5,
    title: "Referral Network",
    location: "BROKERAGE",
    year: "2024",
    category: "ENGINE 03",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722377/img/referral_network.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722377/img/referral_network.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722377/img/referral_network.png-wvc-sizes",
    alt: "Professional referral network collaboration",
    span: "lg:col-span-6",
    aspect: "aspect-[4/3]"
  }, {
    id: 6,
    title: "Gap Equity",
    location: "FINANCING",
    year: "2024",
    category: "ENGINE 01",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722377/img/gap_equity_funding.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722377/img/gap_equity_funding.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1722377/img/gap_equity_funding.png-wvc-sizes",
    alt: "Gap equity funding structure",
    span: "lg:col-span-6",
    aspect: "aspect-[4/3]"
  }];
  return <section data-section-id="506" id="market-authority" className="relative bg-background text-foreground py-24 md:py-32 lg:py-[120px] overflow-hidden">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-16 md:mb-24 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="font-mono text-xs tracking-widest text-muted-foreground uppercase">
                03 — Capital Engines
              </span>
              <span className="h-px w-12 bg-border" aria-hidden="true" />
            </div>
            <h2 className="font-default font-semibold text-foreground leading-[1.05] tracking-tight text-[clamp(2.5rem,5vw,4rem)]">
              Architecting Capital
            </h2>
          </div>
          <div className="lg:col-span-5 lg:pt-4 flex items-end">
            <p className="font-default text-lg text-muted-foreground leading-relaxed max-w-md">
              BeneWealth Financial operates as a multi-engine infrastructure, 
              providing professional-grade capital solutions for real estate 
              investors, partners, and referral networks.
            </p>
          </div>
        </div>

        <div ref={gridRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 transition-all duration-1000 ease-out delay-150 ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {projects.map((project, i) => <article data-index={i} key={project.id} className={`group ${project.span} relative`}>
              <Link to="/CapitalSolutionsPage" className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background" aria-label={`View ${project.title} details`}>
                <div className={`relative overflow-hidden border border-border ${project.aspect} bg-muted`}>
                  <img src={project.image} data-wvc-srcset={project.srcset} data-wvc-sizes={project.sizes} alt={project.alt} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-all duration-[600ms] ease-out group-hover:scale-[1.02] group-hover:saturate-[0.7]" />

                  <div className="absolute inset-x-0 bottom-0 translate-y-full group-hover:translate-y-0 transition-transform duration-[600ms] ease-out bg-foreground/85 backdrop-blur-sm px-5 py-4 flex items-center justify-between">
                    <span className="font-mono text-[11px] tracking-widest text-background uppercase">
                      {project.title.toUpperCase()} — {project.location}
                    </span>
                    <span className="font-mono text-[11px] tracking-widest text-background/80">
                      {project.year}
                    </span>
                  </div>

                  <div className="absolute top-4 left-4 font-mono text-[11px] tracking-widest text-background/90 bg-foreground/40 backdrop-blur-sm px-2 py-1">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                </div>

                <div className="mt-5 flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-mono text-[10px] tracking-widest uppercase text-primary">
                        {project.category}
                      </span>
                      <span className="h-px flex-1 bg-border" aria-hidden="true" />
                      <span className="font-mono text-[10px] tracking-widest text-muted-foreground">
                        {project.year}
                      </span>
                    </div>
                    <h3 className="font-default font-semibold text-foreground text-[22px] leading-tight transition-colors duration-300 group-hover:text-primary">
                      {project.title}
                    </h3>
                    <p className="font-mono text-[11px] tracking-wider uppercase text-muted-foreground mt-1">
                      {project.location}
                    </p>
                  </div>
                  <ArrowUpRight className="size-5 text-foreground/60 mt-1 transition-all duration-500 group-hover:text-primary group-hover:-translate-y-0.5 group-hover:translate-x-0.5" aria-hidden="true" />
                </div>
              </Link>
            </article>)}
        </div>

        <div className="mt-20 md:mt-28 flex flex-col items-center gap-6">
          <div className="flex items-center gap-3">
            <span className="h-px w-16 bg-border" aria-hidden="true" />
            <span className="font-mono text-[11px] tracking-widest text-muted-foreground uppercase">
              Infrastructure
            </span>
            <span className="h-px w-16 bg-border" aria-hidden="true" />
          </div>
          <Button asChild size="lg" variant="default" className="rounded-lg h-12 px-8 font-mono text-xs tracking-widest uppercase bg-primary text-primary-foreground hover:bg-primary/90">
            <Link to="/CapitalSolutionsPage">
              Explore All Engines
              <ArrowRight className="size-4 ml-1" aria-hidden="true" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}